import React, { createContext, useEffect, useReducer } from 'react'
import jwtDecode from 'jwt-decode'
import SplashScreen from 'src/components/SplashScreen'
import axios from 'src/utils/axios'
import authApi from '../api/auth'
import { AccountData } from '../data/AccountData'
import rmskincareAPI from '../utils/rmskincareAPI'

const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  account: null,
}

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false
  }

  const decoded = jwtDecode(accessToken)
  const currentTime = Date.now() / 1000

  return decoded.exp > currentTime
}

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken)
    rmskincareAPI.defaults.headers.common.Authorization = `Bearer ${accessToken}`
  } else {
    localStorage.removeItem('accessToken')
    delete rmskincareAPI.defaults.headers.common.Authorization
  }
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, account } = action.payload

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        account,
      }
    }
    case 'LOGIN': {
      const { account } = action.payload

      return {
        ...state,
        isAuthenticated: true,
        account,
      }
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        account: null,
      }
    }
    case 'REGISTER': {
      const { account } = action.payload

      return {
        ...state,
        isAuthenticated: true,
        account,
      }
    }
    default: {
      return { ...state }
    }
  }
}

const AuthContext = createContext({
  ...initialAuthState,
  method: 'JWT',
  login: () => Promise.resolve(),
  logout: () => {},
  register: () => Promise.resolve(),
})

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState)

  const login = async (username, password) => {
    // const response = await axios.post('/api/account/login', { email, password });
    const result = await authApi.login(username, password)

    const { token } = result.data
    let { account } = jwtDecode(token)
    account = AccountData.objectDuplicate(account)

    setSession(token)
    dispatch({
      type: 'LOGIN',
      payload: {
        account,
      },
    })
  }

  const logout = async () => {
    await authApi.logout()
    setSession(null)
    dispatch({ type: 'LOGOUT' })
  }

  const register = async (email, name, password, employeeId) => {
    const response = await axios.post('/api/account/register', {
      email,
      name,
      password,
      employeeId, // 这里的employeeId
    })
    const { accessToken, account } = response.data

    window.localStorage.setItem('accessToken', accessToken)

    dispatch({
      type: 'REGISTER',
      payload: {
        account,
      },
    })
  }

  useEffect(() => {
    const initialise = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken')

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken)

          // const response = await axios.get('/api/account/me');
          const result = await authApi.refreshToken()

          const { token } = result.data
          let { account } = jwtDecode(token)
          account = AccountData.objectDuplicate(account)

          setSession(token)
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: true,
              account,
            },
          })
        } else {
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: false,
              account: null,
            },
          })
        }
      } catch (err) {
        console.error(err)
        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            account: null,
          },
        })
      }
    }

    initialise()
  }, [])

  if (!state.isInitialised) {
    return <SplashScreen />
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
